import { useEffect, useId, useState } from "react";
import { researchCms } from "@/api/researchCms.service";
import styles from "../CreateRankingEntityModal/CreateRankingEntityModal.module.css";

type CreatePracticeGroupModalProps = {
  open: boolean;
  onClose: () => void;
  rankingEntityId: string;
  rankingEntityName?: string;
  onCreated: (group: { id: string; name: string }) => void;
};

export function CreatePracticeGroupModal({
  open,
  onClose,
  rankingEntityId,
  rankingEntityName,
  onCreated,
}: CreatePracticeGroupModalProps) {
  const titleId = useId();
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName("");
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !rankingEntityId) return;
    setCreating(true);
    setError(null);
    try {
      const result = await researchCms.createPracticeGroup({
        name: name.trim(),
        rankingEntityId,
      });
      onCreated(result.researchPracticeGroup);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create practice group");
    } finally {
      setCreating(false);
    }
  }

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div
        className={styles.dialog}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId} className={styles.title}>
          Create practice group
        </h3>
        <p className={styles.description}>
          A practice group organises related practice areas under a ranking entity.
        </p>

        <form onSubmit={handleSubmit} className={styles.form}>
          {rankingEntityName && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="pg-entity">
                Ranking entity
              </label>
              <input
                id="pg-entity"
                className={styles.input}
                value={rankingEntityName}
                disabled
                style={{ opacity: 0.7 }}
              />
            </div>
          )}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="pg-name">
              Name{" "}
              <span aria-hidden="true" style={{ color: "var(--color-danger, #ef4444)" }}>
                *
              </span>
            </label>
            <input
              id="pg-name"
              className={styles.input}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Banking and finance"
              autoFocus
            />
          </div>

          {error && (
            <div className={styles.serverError} role="alert">
              {error}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.createButton}
              disabled={creating || !name.trim()}
            >
              {creating ? "Creating..." : "Create group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
